import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowLeftLineIcon } from '@svg/ArrowLeftLineIcon';
import { CarLineIcon } from '@svg/CarLineIcon';
import { EllipsisVerticalLineIcon } from '@svg/EllipsisVerticalLineIcon';
import { ExternalLinkLineIcon } from '@svg/ExternalLinkLineIcon';
import { LockLineIcon } from '@svg/LockLineIcon';
import { RefreshCwLineIcon } from '@svg/RefreshCwLineIcon';
import { WifiOffLineIcon } from '@svg/WifiOffLineIcon';
import { XLineIcon } from '@svg/XLineIcon';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '@store/hooks';
import { encrypt } from '@src/utils';
import { updateXHeaderId } from '@utils/storage';
import { setIsShowRide } from '@store/slices/rides/ridesSlice';
import {
  selectCachedConversation,
  useGetDriverChatMessagesQuery,
  useGetDriverConversationQuery,
  useLoadOlderDriverChatMessagesMutation,
  useMarkDriverChatReadMutation,
  useSendDriverChatMessageMutation,
} from '@store/rtk/driverChat';
import { selectDriverChat } from '@store/slices/driverChat/driverChatSlice';
import { TooltipWrapper } from '@uikit/TooltipWrapper';
import { DriverAvatar } from '@component/DriverChat/DriverAvatar/DriverAvatar';
import { ChatComposer } from '@component/DriverChat/ChatComposer/ChatComposer';
import { MessageList } from '@component/DriverChat/ChatThread/MessageList';
import { TripCard } from '@component/DriverChat/ChatThread/TripCard';
import { useLiveDriver } from '@component/DriverChat/hooks/useLiveDriver';
import { useOnlineStatus } from '@component/DriverChat/hooks/useOnlineStatus';
import { useLiveChatStatus } from '@component/DriverChat/hooks/useLiveChatStatus';
import {
  createClientMessageId,
  getStatusLabel,
  mergeChatSeed,
} from '@component/DriverChat/helpers';
import {
  ChatMessage,
  DriverChatMode,
  DriverChatSeed,
  DriverConversation,
} from '@component/DriverChat/types';

type ChatThreadProps = {
  /** null while the conversation is still being created for the seed. */
  conversationId: number | null;
  /** Creating the conversation failed (only meaningful without a conversationId). */
  startFailed?: boolean;
  onRetryStart?: () => void;
  seed: DriverChatSeed;
  mode: DriverChatMode;
  titleId: string;
  onClose: () => void;
  onBack?: () => void;
  dataTestId: string;
};

export const ChatThread: FC<ChatThreadProps> = ({
  conversationId,
  startFailed = false,
  onRetryStart,
  seed,
  mode,
  titleId,
  onClose,
  onBack,
  dataTestId,
}) => {
  const dispatch = useDispatch();
  const { segmentId } = seed;
  const isOnline = useOnlineStatus();
  const { connection, segmentDetails } = useAppSelector(selectDriverChat);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    data: thread,
    currentData,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    refetch,
  } = useGetDriverChatMessagesQuery(conversationId ?? skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const isLoading =
    isInitialLoading || (isFetching && currentData === undefined);
  const isStarting = conversationId == null && !startFailed;
  const messages = useMemo(() => thread?.messages ?? [], [thread]);
  const [loadOlder, { isLoading: isLoadingOlder }] =
    useLoadOlderDriverChatMessagesMutation();
  const { data: detail } = useGetDriverConversationQuery(
    conversationId ?? skipToken
  );
  const conversation =
    useAppSelector((state) =>
      conversationId == null
        ? undefined
        : selectCachedConversation(
            state,
            (c) => c.conversationId === conversationId
          )
    ) ?? detail;
  const [sendMessage] = useSendDriverChatMessageMutation();
  const [markRead] = useMarkDriverChatReadMutation();

  const info = useMemo(
    () =>
      mergeChatSeed<DriverConversation | DriverChatSeed>(
        {
          ...(conversation ?? {}),
          ...(detail ?? {}),
          ...(segmentDetails?.[segmentId] ?? {}),
        } as DriverChatSeed,
        seed
      ),
    [conversation, detail, seed, segmentDetails, segmentId]
  );
  // Same live ride status as everywhere else in the app (socketStatus).
  const { statusId, isChatOpen } = useLiveChatStatus(
    segmentId,
    info.statusId,
    detail?.conversationStatus ?? conversation?.conversationStatus
  );
  const isClosed = !isChatOpen;
  const isReconnecting = connection === 'reconnecting';
  const driverName = info.driverFirstName || 'Driver';
  // The SMS goes to the driver's current number (resolved server-side); show the
  // freshest one we have so the dispatcher can see where it is going.
  const { phone: driverPhone, image: driverImage } = useLiveDriver(segmentId, {
    phone: info.driverPhone,
    image: info.driverImage,
  });

  // Capture where "New messages" starts once, when the thread first loads.
  const [firstUnreadId, setFirstUnreadId] = useState<string | null>(null);
  const unreadCapturedRef = useRef(false);
  const unreadCount = conversation?.unreadCount ?? 0;

  // Read receipts are cursor based: the newest real (server-id) driver message.
  const markThreadRead = () => {
    const lastDriverId = messages
      .filter((m) => m.sender === 'DRIVER' && !Number.isNaN(Number(m.id)))
      .reduce((max, m) => Math.max(max, Number(m.id)), 0);
    if (lastDriverId && conversationId != null) {
      markRead({ conversationId, lastReadMessageId: lastDriverId });
    }
  };

  useEffect(() => {
    if (unreadCapturedRef.current || isLoading || !conversation) return;
    unreadCapturedRef.current = true;
    if (unreadCount > 0) {
      const driverMessages = messages.filter((m) => m.sender === 'DRIVER');
      setFirstUnreadId(
        driverMessages[driverMessages.length - unreadCount]?.id ?? null
      );
      markThreadRead();
    }
  }, [isLoading, messages, unreadCount, conversation]);

  // Unread can still grow from a refetch after the thread opened.
  useEffect(() => {
    if (unreadCapturedRef.current && unreadCount > 0) markThreadRead();
  }, [unreadCount]);

  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isMenuOpen]);

  const loadEarlier = () => {
    const oldestId = messages
      .map((m) => Number(m.id))
      .filter((id) => !Number.isNaN(id))
      .reduce((min, id) => Math.min(min, id), Infinity);
    if (Number.isFinite(oldestId) && conversationId != null) {
      loadOlder({ conversationId, beforeMessageId: oldestId });
    }
  };

  const send = (text: string) => {
    if (conversationId == null) return;
    setFirstUnreadId(null);
    sendMessage({
      conversationId,
      text,
      clientMessageId: createClientMessageId(),
    });
  };

  const retry = (message: ChatMessage) =>
    conversationId != null &&
    sendMessage({
      conversationId,
      text: message.text,
      clientMessageId: message.clientMessageId ?? message.id,
    });

  const openRide = () => {
    if (info.rideId == null) return;
    setIsMenuOpen(false);
    dispatch(setIsShowRide(true));
    updateXHeaderId('ride', info.rideId);
    window.open(
      `/rides/information/${encrypt({
        ride: info.rideId,
        selectedSegmentId: segmentId,
      })}`,
      '_blank'
    );
  };

  const presence = isClosed
    ? 'offline'
    : isReconnecting || !isOnline
      ? 'reconnecting'
      : 'online';

  // TODO(backend): leave the conversation server-side. With other users still
  // in the chat only this user drops off; the last user ending it (or status
  // "Departed from drop-off") saves the whole conversation to the segment Notes
  // once. Until that API exists, ending the chat only closes it for this user.
  const endChat = () => onClose();

  const renderStatusLine = () => {
    if (isClosed) return <span>Conversation closed</span>;
    if (!isOnline) return <span className="is-warning">Offline</span>;
    if (isReconnecting)
      return <span className="is-warning">Reconnecting…</span>;
    return <span className="is-live">Live</span>;
  };

  const renderBanner = () => {
    if (isClosed) {
      return (
        <div className="dc-banner is-closed" role="status">
          <LockLineIcon
            width={16}
            height={16}
            strokeWidth={2}
            aria-hidden="true"
          />
          This conversation is read-only. Ride status:{' '}
          {getStatusLabel(statusId).toLowerCase()}.
        </div>
      );
    }
    if (!isOnline) {
      return (
        <div className="dc-banner is-warning" role="status">
          <WifiOffLineIcon
            width={16}
            height={16}
            strokeWidth={2}
            aria-hidden="true"
          />
          You're offline. Reconnect to the internet to send messages.
        </div>
      );
    }
    if (isReconnecting) {
      return (
        <div className="dc-banner is-warning" role="status">
          <RefreshCwLineIcon
            width={16}
            height={16}
            strokeWidth={2}
            className="dc-spin"
            aria-hidden="true"
          />
          Reconnecting… New messages from the driver may be delayed.
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dc-thread" data-test-id={`${dataTestId}-thread`}>
      <div className="dc-thread__header">
        {onBack && (
          <button
            type="button"
            className="dc-icon-button"
            aria-label="Back to conversations"
            onClick={onBack}
          >
            <ArrowLeftLineIcon
              width={18}
              height={18}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </button>
        )}
        <DriverAvatar
          name={driverName}
          image={driverImage}
          presence={presence}
          size={mode === 'panel' ? 'sm' : 'md'}
        />
        <div className="dc-thread__identity">
          <div className="dc-thread__name" id={titleId}>
            <TooltipWrapper tooltip={driverName} disableChildrenWrapper>
              <span className="dc-thread__name-text">{driverName}</span>
            </TooltipWrapper>
            <span className="dc-tag">Driver</span>
          </div>
          <div className="dc-thread__subline">
            {renderStatusLine()}
            {driverPhone && !isClosed && (
              <>
                <span aria-hidden="true">·</span>
                <span
                  className="dc-mono"
                  data-test-id={`${dataTestId}-driver-phone`}
                >
                  {driverPhone}
                </span>
              </>
            )}
            {(info.vehicleLabel || info.licensePlate) && (
              <>
                <span aria-hidden="true">·</span>
                <CarLineIcon
                  width={14}
                  height={14}
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
                <TooltipWrapper
                  tooltip={[info.vehicleLabel, info.licensePlate]
                    .filter(Boolean)
                    .join(' · ')}
                  disableChildrenWrapper
                >
                  <span className="dc-thread__vehicle">
                    {info.vehicleLabel}
                    {info.vehicleLabel && info.licensePlate ? ' · ' : ''}
                    {info.licensePlate && (
                      <span className="dc-mono">{info.licensePlate}</span>
                    )}
                  </span>
                </TooltipWrapper>
              </>
            )}
          </div>
        </div>
        <div className="dc-thread__actions">
          {!isClosed && (
            <button
              type="button"
              className="dc-link"
              onClick={endChat}
              data-test-id={`${dataTestId}-end-chat`}
            >
              End Chat
            </button>
          )}
          {info.rideId != null && (
            <div className="dc-menu" ref={menuRef}>
              <button
                type="button"
                className="dc-icon-button"
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen((open) => !open)}
              >
                <EllipsisVerticalLineIcon
                  width={18}
                  height={18}
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              </button>
              {isMenuOpen && (
                <div className="dc-menu__list" role="menu">
                  <button type="button" role="menuitem" onClick={openRide}>
                    <ExternalLinkLineIcon
                      width={15}
                      height={15}
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                    Open ride details
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="dc-icon-button"
            aria-label="Close chat"
            onClick={onClose}
          >
            <XLineIcon
              width={18}
              height={18}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <TripCard
        info={info}
        statusId={statusId}
        isClosed={isClosed}
        dataTestId={dataTestId}
      />
      {renderBanner()}

      <MessageList
        messages={messages}
        firstUnreadId={firstUnreadId}
        isLoading={isLoading || isStarting}
        isError={isError || (conversationId == null && startFailed)}
        driverName={driverName}
        driverImage={driverImage}
        hasMore={!!thread?.hasMore}
        isLoadingOlder={isLoadingOlder}
        onLoadOlder={loadEarlier}
        onReload={conversationId == null ? (onRetryStart ?? refetch) : refetch}
        onRetry={retry}
      />

      {isClosed && conversationId != null ? (
        <div className="dc-thread__closed">
          <LockLineIcon
            width={14}
            height={14}
            strokeWidth={2}
            aria-hidden="true"
          />
          Conversation closed
        </div>
      ) : (
        <ChatComposer
          conversationId={conversationId}
          driverFirstName={driverName}
          disabled={!isOnline || conversationId == null}
          disabledPlaceholder={
            isOnline ? 'Connecting…' : 'Waiting for connection…'
          }
          onSend={send}
          dataTestId={dataTestId}
        />
      )}
    </div>
  );
};
